import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Session } from './Session';
import { TaxplanIQServices } from './TaxplanIQServices';
import { SOURCE_TYPE } from '../constants/Tpiq.constants';

export interface DoceboSSOResponse {
  url: string;
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DoceboSsoService extends TaxplanIQServices {

  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Build the SSO service path for the current session.
   * Note: All Java service paths include /TaxPlanningServices prefix
   */
  private getSsoPath(orgId: string, userId: string): string {
    return `/TaxPlanningServices/org/${orgId}/users/${encodeURIComponent(userId)}/sso/docebo`;
  }

  /**
   * Generate SSO URL and redirect to Docebo
   * Gets user info from session/localStorage
   */
  redirectToDocebo(): void {
    // Get current user info from session
    const user = Session.getUser();
    const userId = user?.id;
    const orgId = Session.getOrg();
    const token = Session.getToken();

    if (!userId || !orgId || !token) {
      console.error('User not logged in or missing org info/token', { userId, orgId, hasToken: !!token });
      alert('Please log in to access the TaxPlanIQ Academy.');
      return;
    }

    const servicePath = this.getSsoPath(orgId, userId);

    // Log status text only. The path carries the user id, and the SSO url
    // carries a live Docebo access token.
    console.log('Calling Docebo SSO endpoint');

    // Open the new tab now, inside the click event.
    // The browser blocks window.open in an async callback, so the tab must
    // exist before the request starts. The SSO URL loads into it on response.
    // The tab cannot use the noopener feature, because that returns null and
    // this flow needs the handle, so clear the opener by hand instead.
    const academyTab: Window | null = window.open('', '_blank');
    if (academyTab) {
      academyTab.opener = null;
    }

    // Call backend to generate SSO URL
    this.query(servicePath, SOURCE_TYPE.JAVA).subscribe({
      next: (response: DoceboSSOResponse) => {
        if (response.success && response.url) {
          console.log('Redirecting to Docebo');
          if (academyTab && !academyTab.closed) {
            academyTab.location.href = response.url;
          } else if (!academyTab) {
            // The browser blocked the tab. A second window.open here is blocked
            // for the same reason, so use the current window instead.
            window.location.assign(response.url);
          } else {
            // The user closed the tab while the request was in flight. Treat
            // that as a cancel and leave the current page alone.
            console.log('Academy tab was closed before the SSO url arrived');
          }
        } else {
          console.error('Failed to generate SSO URL:', response.message);
          this.closeTab(academyTab);
          alert('Failed to generate SSO URL: ' + response.message);
        }
      },
      error: (error: any) => {
        console.error('Failed to redirect to Docebo:', error);
        this.closeTab(academyTab);
        alert('Unable to access TaxPlanIQ Academy. Please try again later.');
      }
    });
  }

  /**
   * Close the pre-opened tab so a failed call leaves no blank tab behind.
   */
  private closeTab(tab: Window | null): void {
    if (tab && !tab.closed) {
      tab.close();
    }
  }

  /**
   * Get SSO URL without redirecting (for custom handling)
   */
  getSsoUrl(): Observable<DoceboSSOResponse> {
    const user = Session.getUser();
    const userId = user?.id;
    const orgId = Session.getOrg();

    if (!userId || !orgId) {
      throw new Error('User not logged in or missing org info');
    }

    return this.query(this.getSsoPath(orgId, userId), SOURCE_TYPE.JAVA);
  }
}

